import { Component } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Form, Button, Alert, Table, Spinner, Badge } from "react-bootstrap";
import { FaUserPlus, FaCheck, FaTimes, FaFilePdf, FaExclamationTriangle } from "react-icons/fa";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../services/api";
import { IMaskInput } from "react-imask";
import { withRouter } from "../../common/with-router";
import { withTranslation } from "react-i18next";
import { getCurrentUser } from "../../common/utils";
import { formataDataHoraExibicao } from "../../common/utils";

const MySwal = withReactContent(Swal);

const CONSELHOS = ["CRN", "COREN", "CREFITO", "CRP", "CRF", "CREF", "Outro"];

// Canais de contato do programa exibidos após a indicação (entram no texto por interpolação).
const CONTATO_TELEFONE = "0800 590 0744";
const CONTATO_WHATSAPP = "(11) 94362-9941";
const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB",
             "PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

/**
 * Indicação de Profissional de Saúde, na visão do médico.
 *
 * O médico indica quem vai acompanhar seus pacientes durante o teste e assina a
 * declaração. Antes de uma nova indicação, o documento manda revisar as anteriores —
 * é o passo de "manter ou encerrar" que abre a tela quando já existe indicação.
 *
 * Indicação nunca é excluída: encerrar muda o status e o registro continua no histórico.
 */
class IndicacoesProfissionais extends Component {
    constructor(props) {
        super(props);
        this.state = {
            carregando: true,
            erro: "",
            programa: null,
            indicacoes: [],
            // revisão das indicações anteriores, exigida antes de indicar de novo
            revisando: false,
            mostrarFormulario: false,
            salvando: false,
            form: this.formVazio(),
            erros: {},
        };
    }

    formVazio = () => ({
        nome_indicado: "",
        conselho: "",
        registro: "",
        registro_uf: "",
        telefone: "",
        email: "",
        autoriza_contato: null,
        assino: false,
    });

    componentDidMount() {
        this.carregar();
    }

    programaId = () => getCurrentUser()?.["ambiente_programa_id"] || "";

    carregar = async () => {
        const programaId = this.programaId();
        if (!programaId) {
            this.setState({ carregando: false, erro: this.props.t("indicacoes:profissionais.errors.noProgram") });
            return;
        }

        try {
            const { data } = await api.get(`/pessoas/indicacoes-profissionais/minhas/?programa_id=${programaId}`);
            const indicacoes = data.indicacoes || [];
            const ativas = indicacoes.filter((i) => i.status === "ativa");
            this.setState({
                carregando: false,
                programa: data.programa,
                indicacoes,
                // com indicação ativa, a tela abre pela revisão; sem nenhuma, vai direto ao formulário
                revisando: ativas.length > 0,
                mostrarFormulario: ativas.length === 0,
            });
        } catch (error) {
            const detalhe = error.response?.data?.detail;
            this.setState({
                carregando: false,
                erro: detalhe || this.props.t("indicacoes:profissionais.errors.load"),
            });
        }
    };

    /** IMaskInput devolve o valor, nao o evento -- mesmo padrao do cadastro de médico. */
    handleMaskAccept = (campo) => (valor) => {
        this.setState((prev) => ({
            form: { ...prev.form, [campo]: valor },
            erros: { ...prev.erros, [campo]: "" },
        }));
    };

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState((prev) => ({ form: { ...prev.form, [name]: value }, erros: { ...prev.erros, [name]: "" } }));
    };

    /** Encerra a indicação sem apagá-la: o registro fica no histórico. */
    encerrarIndicacao = async (indicacao) => {
        const { t } = this.props;
        const escolha = await MySwal.fire({
            title: t("indicacoes:profissionais.close.title"),
            html: t("indicacoes:profissionais.close.text", { nome: indicacao.nome_indicado }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("indicacoes:profissionais.close.confirm"),
            cancelButtonText: t("indicacoes:profissionais.close.keep"),
        });
        if (!escolha.isConfirmed) return;

        try {
            // rota do próprio médico: a de operação exige permissão que ele não tem (dava 403)
            await api.put(`/pessoas/indicacoes-profissionais/minhas/${indicacao.id}/`, {
                motivo_cancelamento: "Encerrada pelo médico na revisão das indicações.",
            });
            await this.carregar();
        } catch (error) {
            console.error("Erro ao encerrar a indicação:", error);
            MySwal.fire({ icon: "error", title: t("common:alert.attention"), showCloseButton: true,
                text: t("indicacoes:profissionais.errors.close") });
        }
    };

    validar = () => {
        const { form } = this.state;
        const erros = {};
        // obrigatórios conforme o documento; e-mail é o único opcional. Guarda as chaves, que
        // são traduzidas no render (acompanham a troca de idioma).
        if (!form.nome_indicado.trim()) erros.nome_indicado = "indicacoes:profissionais.validation.name";
        if (!form.conselho) erros.conselho = "indicacoes:profissionais.validation.council";
        if (!form.registro.trim()) erros.registro = "indicacoes:profissionais.validation.registration";
        if (!form.registro_uf) erros.registro_uf = "indicacoes:profissionais.validation.state";
        if (!String(form.telefone || "").trim()) erros.telefone = "indicacoes:profissionais.validation.phone";
        if (form.autoriza_contato === null) erros.autoriza_contato = "indicacoes:assinatura.errors.authorization";
        if (!form.assino) erros.assino = "indicacoes:assinatura.errors.confirm";
        this.setState({ erros });
        return Object.keys(erros).length === 0;
    };

    enviar = async (e) => {
        e.preventDefault();
        const { t } = this.props;
        if (!this.validar()) return;

        this.setState({ salvando: true });
        try {
            // `assino` é confirmação de tela: quem registra a assinatura é o backend
            const dados = { ...this.state.form };
            delete dados.assino;
            const { data } = await api.post("/pessoas/indicacoes-profissionais/minhas/", {
                programa_id: this.programaId(),
                ...dados,
            });

            await MySwal.fire({
                icon: "success",
                title: t("indicacoes:profissionais.sent.title"),
                html: t("indicacoes:profissionais.sent.text", { telefone: CONTATO_TELEFONE, whatsapp: CONTATO_WHATSAPP }),
                confirmButtonText: t("indicacoes:assinatura.understood"),
                // a declaração assinada é a evidência do ato: fica à mão logo depois de assinar
                showDenyButton: !!data?.declaracao_url,
                denyButtonText: t("indicacoes:profissionais.sent.viewSigned"),
                denyButtonColor: "#6c757d",
            }).then((escolha) => {
                if (escolha.isDenied && data?.declaracao_url) {
                    window.open(data.declaracao_url, "_blank", "noopener");
                }
            });

            this.setState({ salvando: false, form: this.formVazio(), mostrarFormulario: false });
            await this.carregar();
        } catch (error) {
            console.error("Erro ao enviar a indicação:", error);
            this.setState({ salvando: false });
            // o backend explica o motivo (ex.: profissional já indicado e ativo); engolir a
            // mensagem deixava o médico sem saber o que fazer -- e "tente novamente" é
            // conselho errado, porque repetir falha igual
            const dados = error.response?.data || {};
            const detalhe = dados.detail || Object.values(dados).flat()[0];
            MySwal.fire({ icon: "error", title: t("common:alert.attention"), showCloseButton: true,
                text: detalhe || t("indicacoes:profissionais.errors.save") });
        }
    };

    renderStatus = (status) => {
        const { t } = this.props;
        const cores = { ativa: "success", cancelada: "secondary", rascunho: "warning" };
        return (
            <Badge bg={cores[status] || "light"}>
                {t(`indicacoes:profissionais.status.${status}`, { defaultValue: status })}
            </Badge>
        );
    };

    /** Onde a indicação parou: o médico assina a declaração, mas quem aceita o termo é o
     *  profissional indicado -- sem isto ele não sabia se ainda faltava alguma coisa. */
    renderStatusTermo = (i) => {
        const { t } = this.props;
        if (i.status === "cancelada") return <span className="text-muted small">—</span>;
        if (i.data_aceite_termo) {
            return <Badge bg="success">{t("indicacoes:profissionais.term.acceptedOn", { data: formataDataHoraExibicao(i.data_aceite_termo) })}</Badge>;
        }
        if (!i.dados_indicado) return <Badge bg="info">{t("indicacoes:profissionais.term.awaitingRegistration")}</Badge>;
        return <Badge bg="warning">{t("indicacoes:profissionais.term.awaitingAcceptance")}</Badge>;
    };

    /** Declaração assinada daquela indicação. A URL vem assinada do backend e expira. */
    renderLinkDeclaracao = (indicacao) => {
        if (!indicacao.declaracao_url) return <span className="text-muted small">—</span>;
        return (
            <Button variant="light" size="sm" href={indicacao.declaracao_url}
                    target="_blank" rel="noopener noreferrer">
                <FaFilePdf className="me-1" /> {this.props.t("indicacoes:profissionais.view")}
            </Button>
        );
    };

    renderCabecalhoTabela = () => {
        const { t } = this.props;
        return (
            <>
                <th>{t("indicacoes:profissionais.table.professional")}</th>
                <th>{t("indicacoes:profissionais.table.council")}</th>
                <th>{t("indicacoes:profissionais.table.referredOn")}</th>
                <th>{t("indicacoes:profissionais.table.status")}</th>
                <th>{t("indicacoes:profissionais.table.term")}</th>
            </>
        );
    };

    renderRevisao = () => {
        const { t } = this.props;
        const ativas = this.state.indicacoes.filter((i) => i.status === "ativa");
        if (!ativas.length) return null;

        return (
            <Card className="cardDestaque mb-4">
                <Card.Body>
                    <h5>{t("indicacoes:profissionais.review.title")}</h5>
                    <p className="text-muted">
                        {t("indicacoes:profissionais.review.hint")}
                    </p>

                    <Table responsive className="custom-table">
                        <thead>
                            <tr>
                                {this.renderCabecalhoTabela()}
                                <th>{t("indicacoes:profissionais.table.declaration")}</th>
                                <th className="text-end">{t("indicacoes:profissionais.review.keep")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ativas.map((i) => (
                                <tr key={i.id}>
                                    <td>{i.nome_indicado}</td>
                                    <td>{[i.conselho, i.registro, i.registro_uf].filter(Boolean).join(" ") || "-"}</td>
                                    <td>{i.data_indicacao ? formataDataHoraExibicao(i.data_indicacao) : "-"}</td>
                                    <td>{this.renderStatus(i.status)}</td>
                                    <td>{this.renderStatusTermo(i)}</td>
                                    <td>{this.renderLinkDeclaracao(i)}</td>
                                    <td className="text-end text-nowrap">
                                        <Button variant="outline-danger" size="sm" onClick={() => this.encerrarIndicacao(i)}>
                                            <FaTimes className="me-1" /> {t("indicacoes:profissionais.review.close")}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <div className="mt-3">
                        <Button variant="success" onClick={() => this.setState({ mostrarFormulario: true })}>
                            <FaUserPlus className="me-1" /> {t("indicacoes:profissionais.review.referAnother")}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    renderFormulario = () => {
        const { t } = this.props;
        const { form, erros, salvando, programa } = this.state;
        // os erros guardam chaves de tradução
        const erro = (campo) => (erros[campo] ? t(erros[campo]) : "");

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <h5>{t("indicacoes:profissionais.form.title")}</h5>
                    <Form onSubmit={this.enviar}>
                        <Row>
                            <Col sm={6} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("indicacoes:profissionais.form.name")} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text" name="nome_indicado" value={form.nome_indicado}
                                                  onChange={this.handleChange} isInvalid={!!erros.nome_indicado} />
                                    <Form.Control.Feedback type="invalid">{erro("nome_indicado")}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={3} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("indicacoes:profissionais.form.council")} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select name="conselho" value={form.conselho} onChange={this.handleChange}
                                                 isInvalid={!!erros.conselho}>
                                        <option value="">{t("indicacoes:profissionais.form.select")}</option>
                                        {/* o valor gravado não muda; só o rótulo de "Outro" é traduzido */}
                                        {CONSELHOS.map((c) => (
                                            <option key={c} value={c}>{c === "Outro" ? t("indicacoes:profissionais.form.otherCouncil") : c}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{erro("conselho")}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={3} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("indicacoes:profissionais.form.state")} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select name="registro_uf" value={form.registro_uf} onChange={this.handleChange}
                                                 isInvalid={!!erros.registro_uf}>
                                        <option value="">{t("indicacoes:profissionais.form.state")}</option>
                                        {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{erro("registro_uf")}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={4} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("indicacoes:profissionais.form.registration")} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text" name="registro" value={form.registro}
                                                  onChange={this.handleChange} isInvalid={!!erros.registro} />
                                    <Form.Control.Feedback type="invalid">{erro("registro")}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={4} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("indicacoes:profissionais.form.phone")} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control as={IMaskInput} unmask="typed" mask="(00) 00000-0000"
                                                  name="telefone" value={form.telefone}
                                                  onAccept={this.handleMaskAccept("telefone")}
                                                  isInvalid={!!erros.telefone} />
                                    <Form.Control.Feedback type="invalid">{erro("telefone")}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={4} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("indicacoes:profissionais.form.email")}</Form.Label>
                                    <Form.Control type="email" name="email" value={form.email} onChange={this.handleChange} />
                                    <Form.Text muted>{t("indicacoes:profissionais.form.optional")}</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                {t("indicacoes:assinatura.authorizationText")}
                            </Form.Label>
                            <div>
                                {[["sim", true], ["nao", false]].map(([chave, valor]) => (
                                    <Form.Check inline type="radio" key={chave} id={`autoriza_${chave}`}
                                                name="autoriza_contato"
                                                label={valor ? t("indicacoes:assinatura.yes") : t("indicacoes:assinatura.no")}
                                                checked={form.autoriza_contato === valor}
                                                onChange={() => this.setState((prev) => ({
                                                    form: { ...prev.form, autoriza_contato: valor },
                                                    erros: { ...prev.erros, autoriza_contato: "" },
                                                }))} />
                                ))}
                            </div>
                            {erros.autoriza_contato && (
                                <div className="text-danger small mt-1">{erro("autoriza_contato")}</div>
                            )}
                        </Form.Group>

                        {/* assinar sem poder ler o documento seria assinar no escuro: o PDF do
                            programa abre aqui, e a confirmação só libera depois */}
                        <Alert variant="light" className="border">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                <div>
                                    <strong>{t("indicacoes:assinatura.declaration")}</strong>
                                    <div className="text-muted small">
                                        {t("indicacoes:assinatura.declarationHint")}
                                    </div>
                                </div>
                                {programa?.declaracao_url && (
                                    <Button variant="outline-secondary" size="sm" href={programa.declaracao_url}
                                            target="_blank" rel="noopener noreferrer">
                                        <FaFilePdf className="me-1" /> {t("indicacoes:assinatura.readDeclaration")}
                                    </Button>
                                )}
                            </div>

                            <Form.Check
                                type="checkbox" id="assino_declaracao" className="mt-3"
                                label={t("indicacoes:profissionais.form.signCheckbox")}
                                checked={form.assino}
                                onChange={() => this.setState((prev) => ({
                                    form: { ...prev.form, assino: !prev.form.assino },
                                    erros: { ...prev.erros, assino: "" },
                                }))}
                            />
                            {erros.assino && <div className="text-danger small mt-1">{erro("assino")}</div>}
                        </Alert>

                        <Button variant="success" type="submit" className="btn-submit" disabled={salvando}>
                            {salvando ? <span className="spinner-border spinner-border-sm"></span>
                                      : <><FaCheck className="me-1" /> {t("indicacoes:profissionais.form.submit")}</>}
                        </Button>
                        {this.state.indicacoes.some((i) => i.status === "ativa") && (
                            <Button variant="link" className="ms-2"
                                    onClick={() => this.setState({ mostrarFormulario: false })}>
                                {t("common:button.cancel")}
                            </Button>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    renderHistorico = () => {
        const { t } = this.props;
        const encerradas = this.state.indicacoes.filter((i) => i.status !== "ativa");
        if (!encerradas.length) return null;

        return (
            <Card className="cardDestaque mt-4">
                <Card.Body>
                    <h6 className="text-muted">{t("indicacoes:profissionais.history")}</h6>
                    <Table responsive className="custom-table mt-2">
                        <thead>
                            <tr>
                                {this.renderCabecalhoTabela()}
                                <th className="text-end">{t("indicacoes:profissionais.table.declaration")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {encerradas.map((i) => (
                                <tr key={i.id}>
                                    <td>{i.nome_indicado}</td>
                                    <td>{[i.conselho, i.registro, i.registro_uf].filter(Boolean).join(" ") || "-"}</td>
                                    <td>{i.data_indicacao ? formataDataHoraExibicao(i.data_indicacao) : "-"}</td>
                                    <td>{this.renderStatus(i.status)}</td>
                                    <td>{this.renderStatusTermo(i)}</td>
                                    <td className="text-end">{this.renderLinkDeclaracao(i)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    };

    render() {
        const { t } = this.props;
        const { carregando, erro, mostrarFormulario } = this.state;

        if (carregando) {
            return (
                <div className="text-center mt-5">
                    <Spinner animation="border" role="status"><span className="visually-hidden">{t("common:label.loading")}</span></Spinner>
                    <p className="mt-2">{t("indicacoes:profissionais.loading")}</p>
                </div>
            );
        }

        if (erro) {
            return (
                <Alert variant="warning" className="mt-4">
                    <FaExclamationTriangle className="me-2" />{erro}
                </Alert>
            );
        }

        return (
            <div>
                <Row className="align-items-center mb-3">
                    <Col sm={8} lg={8}>
                        <h4 className="page-title mb-0">{t("indicacoes:profissionais.pageTitle")}</h4>
                        {/* a faixa do topo é escura: `text-muted` some nela (mesmo caso de Feriados) */}
                        <small className="text-white">
                            {t("indicacoes:profissionais.pageSubtitle")}
                        </small>
                    </Col>
                </Row>

                {!mostrarFormulario && this.renderRevisao()}
                {mostrarFormulario && this.renderFormulario()}
                {this.renderHistorico()}
            </div>
        );
    }
}

IndicacoesProfissionais.propTypes = {
    t: PropTypes.func.isRequired,
    router: PropTypes.object,
};

export default withTranslation(["indicacoes", "common"])(withRouter(IndicacoesProfissionais));
